import React, { useState } from 'react';
import { Icon, Button, Badge } from '@/components/atoms';
import { useUpdatePurchaseOrderStatusMutation } from '@/hooks/useErpApi';
import { useDispatch } from 'react-redux';
import { updatePurchaseOrder } from '@/features/purchaseOrders';
import type { PurchaseOrder } from '@/schemas/purchaseOrders';
import { DeliveryModal } from './DeliveryModal';
import { DeliveryDateModal } from './DeliveryDateModal';

interface PurchaseOrderDetailProps {
  purchaseOrder: PurchaseOrder;
  onUpdate?: () => void;
}

export const PurchaseOrderDetail: React.FC<PurchaseOrderDetailProps> = ({
  purchaseOrder,
  onUpdate,
}) => {
  const dispatch = useDispatch();
  const { mutate: updateStatus, isLoading } = useUpdatePurchaseOrderStatusMutation();
  const [isGRNModalOpen, setIsGRNModalOpen] = useState(false);
  const [isDeliveryDateModalOpen, setIsDeliveryDateModalOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedPurchaseOrder, setUpdatedPurchaseOrder] = useState<PurchaseOrder | null>(null);

  const statusFlow = ['placed', 'confirmed', 'out_for_delivery', 'delivered', 'verified', 'paid'];
  const currentStatusIndex = statusFlow.indexOf(purchaseOrder.status);

  const getStatusBadge = (status: PurchaseOrder['status']) => {
    switch (status) {
      case 'placed':
        return <Badge variant="info">Placed</Badge>;
      case 'confirmed':
        return <Badge variant="warning">Confirmed</Badge>;
      case 'out_for_delivery':
        return <Badge variant="secondary">Out for Delivery</Badge>;
      case 'delivered':
        return <Badge variant="success">Delivered</Badge>;
      case 'verified':
        return <Badge variant="success">Verified</Badge>;
      case 'paid':
        return <Badge variant="secondary">Paid</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    try {
      setError(null);
      const result = await updateStatus({
        id: purchaseOrder.id,
        data: { status: newStatus },
      });
      dispatch(updatePurchaseOrder(result as never));
      onUpdate?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const handleOpenGRN = () => {
    setIsGRNModalOpen(true);
  };

  const handleGRNSuccess = () => {
    setIsGRNModalOpen(false);
    onUpdate?.();
    // Toast notification is handled in DeliveryModal
  };

  const handleVerifyClick = async () => {
    try {
      setError(null);
      // First update status to verified and wait for completion
      const result = await updateStatus({
        id: purchaseOrder.id,
        data: { status: 'verified' },
      });
      dispatch(updatePurchaseOrder(result as never));
      // Store the updated purchase order to pass to modal
      setUpdatedPurchaseOrder(result as PurchaseOrder);
      // Only after status is verified, open GRN modal
      // Don't call onUpdate here to avoid refetch that might close the modal
      setIsVerifyModalOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify purchase order');
      // Don't open modal if status update failed
    }
  };

  const handleVerifySuccess = () => {
    // This is called only after GRN is created successfully (not after verification)
    // The modal stays open after verification so user can create GRN
    setIsVerifyModalOpen(false);
    onUpdate?.();
  };

  const canConfirm = purchaseOrder.status === 'placed';
  const canMarkShipped = purchaseOrder.status === 'confirmed';
  const canMarkDelivered = purchaseOrder.status === 'out_for_delivery';
  const canReceive = false; // Disabled - use Mark as Delivered instead
  const canVerify = purchaseOrder.status === 'delivered';
  const canMarkPaid = purchaseOrder.status === 'verified';

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">{purchaseOrder.po_number}</h2>
            <p className="text-neutral-600 mt-1">
              Ordered on {new Date(purchaseOrder.order_date).toLocaleDateString()}
            </p>
          </div>
          {getStatusBadge(purchaseOrder.status)}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div>
            <p className="text-sm text-neutral-600 mb-1">Supplier</p>
            <p className="text-base font-medium text-neutral-900">
              {purchaseOrder.collaborator_name}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Warehouse</p>
            <p className="text-base font-medium text-neutral-900">{purchaseOrder.warehouse_name}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Expected Delivery</p>
            <p className="text-base font-medium text-neutral-900">
              {new Date(purchaseOrder.expected_delivery_date).toLocaleDateString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Total Amount</p>
            <p className="text-base font-semibold text-neutral-900">
              ₹{purchaseOrder.total_amount.toFixed(2)}
            </p>
          </div>
        </div>

        {/* NEW: GST Totals Display */}
        {((purchaseOrder as any).total_base_amount !== undefined ||
          (purchaseOrder as any).total_gst_amount !== undefined) && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-neutral-900 mb-3">GST Breakdown</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {(purchaseOrder as any).total_base_amount !== undefined && (
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Base Amount</p>
                  <p className="text-sm font-medium text-neutral-900">
                    ₹{((purchaseOrder as any).total_base_amount || 0).toFixed(2)}
                  </p>
                </div>
              )}
              {(purchaseOrder as any).total_gst_amount !== undefined && (
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Total GST</p>
                  <p className="text-sm font-medium text-neutral-900">
                    ₹{((purchaseOrder as any).total_gst_amount || 0).toFixed(2)}
                  </p>
                </div>
              )}
              {(purchaseOrder as any).is_inter_state === false &&
                (purchaseOrder as any).total_cgst_amount !== undefined && (
                  <>
                    <div>
                      <p className="text-xs text-neutral-600 mb-1">CGST</p>
                      <p className="text-sm font-medium text-neutral-900">
                        ₹{((purchaseOrder as any).total_cgst_amount || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-neutral-600 mb-1">SGST</p>
                      <p className="text-sm font-medium text-neutral-900">
                        ₹{((purchaseOrder as any).total_sgst_amount || 0).toFixed(2)}
                      </p>
                    </div>
                  </>
                )}
              {(purchaseOrder as any).is_inter_state === true &&
                (purchaseOrder as any).total_igst_amount !== undefined && (
                  <div>
                    <p className="text-xs text-neutral-600 mb-1">IGST</p>
                    <p className="text-sm font-medium text-neutral-900">
                      ₹{((purchaseOrder as any).total_igst_amount || 0).toFixed(2)}
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* New fields display */}
        {(purchaseOrder as any).total_rejected_amount !== undefined ||
        (purchaseOrder as any).amount_owed !== undefined ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-neutral-200">
            {(purchaseOrder as any).total_rejected_amount !== undefined && (
              <div>
                <p className="text-sm text-neutral-600 mb-1">Rejected Amount</p>
                <p className="text-base font-medium text-error-600">
                  ₹{((purchaseOrder as any).total_rejected_amount || 0).toFixed(2)}
                </p>
              </div>
            )}
            {(purchaseOrder as any).amount_owed !== undefined && (
              <div>
                <p className="text-sm text-neutral-600 mb-1">Amount Owed</p>
                <p className="text-base font-semibold text-neutral-900">
                  ₹{((purchaseOrder as any).amount_owed || 0).toFixed(2)}
                </p>
              </div>
            )}
            {(purchaseOrder as any).is_inter_state !== undefined &&
              (purchaseOrder as any).is_inter_state !== null && (
                <div>
                  <p className="text-sm text-neutral-600 mb-1">GST Type</p>
                  <p className="text-base font-medium text-neutral-900">
                    {(purchaseOrder as any).is_inter_state ? 'IGST (Inter-state)' : 'CGST+SGST (Intra-state)'}
                  </p>
                </div>
              )}
          </div>
        ) : null}

        <div className="border-t border-neutral-200 pt-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Status Timeline</h3>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-neutral-200 -z-10"></div>
            {statusFlow.map((status, index) => {
              const isCompleted = index <= currentStatusIndex;
              const isCurrent = index === currentStatusIndex;
              return (
                <div key={status} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted
                        ? isCurrent
                          ? 'bg-primary-600'
                          : 'bg-success-600'
                        : 'bg-neutral-200'
                    } text-white mb-2`}
                  >
                    {isCompleted && !isCurrent ? (
                      <Icon name="Check" size={20} />
                    ) : (
                      <span className="text-sm font-semibold">{index + 1}</span>
                    )}
                  </div>
                  <p
                    className={`text-xs font-medium ${
                      isCompleted ? 'text-neutral-900' : 'text-neutral-500'
                    } text-center`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {purchaseOrder.actual_delivery_date && (
          <div className="mt-6 border-t border-neutral-200 pt-4">
            <p className="text-sm text-neutral-600">
              Delivered on {new Date(purchaseOrder.actual_delivery_date).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Items</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  Product
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase">
                  SKU
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                  Quantity
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                  Unit Price
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                  Line Total
                </th>
                {purchaseOrder.status !== 'placed' && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                    Received
                  </th>
                )}
                {(purchaseOrder.status === 'verified' || purchaseOrder.status === 'paid') && (
                  <>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      Base Price
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500 uppercase">
                      GST
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {purchaseOrder.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-sm text-neutral-900">{item.product_name}</td>
                  <td className="px-4 py-3 text-sm text-neutral-600">{item.product_sku}</td>
                  <td className="px-4 py-3 text-sm text-neutral-900 text-right">{item.quantity}</td>
                  <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                    ₹{item.unit_price.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-neutral-900 text-right">
                    ₹{item.line_total.toFixed(2)}
                  </td>
                  {purchaseOrder.status !== 'placed' && (
                    <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                      {item.received_quantity}
                    </td>
                  )}
                  {(purchaseOrder.status === 'verified' || purchaseOrder.status === 'paid') && (
                    <>
                      <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                        ₹{((item as any).base_price || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-900 text-right">
                        {(purchaseOrder as any).is_inter_state
                          ? `IGST: ₹${((item as any).igst_amount_total || 0).toFixed(2)}`
                          : (purchaseOrder as any).is_inter_state === false
                            ? `CGST: ₹${((item as any).cgst_amount_total || 0).toFixed(2)} + SGST: ₹${((item as any).sgst_amount_total || 0).toFixed(2)}`
                            : `GST: ₹${((item as any).gst_amount_total || 0).toFixed(2)}`}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-neutral-50 border-t-2 border-neutral-200">
              <tr>
                <td
                  colSpan={
                    purchaseOrder.status === 'placed'
                      ? 4
                      : purchaseOrder.status === 'verified' || purchaseOrder.status === 'paid'
                        ? 7
                        : 5
                  }
                  className="px-4 py-3 text-right text-sm font-semibold text-neutral-900"
                >
                  Total Amount:
                </td>
                <td className="px-4 py-3 text-right text-base font-bold text-neutral-900">
                  ₹{purchaseOrder.total_amount.toFixed(2)}
                </td>
              </tr>
              {(purchaseOrder as any).total_rejected_amount !== undefined &&
                (purchaseOrder as any).total_rejected_amount > 0 && (
                  <tr>
                    <td
                      colSpan={
                        purchaseOrder.status === 'placed'
                          ? 4
                          : purchaseOrder.status === 'verified' || purchaseOrder.status === 'paid'
                            ? 7
                            : 5
                      }
                      className="px-4 py-3 text-right text-sm font-semibold text-error-600"
                    >
                      Rejected Amount:
                    </td>
                    <td className="px-4 py-3 text-right text-base font-bold text-error-600">
                      -₹{((purchaseOrder as any).total_rejected_amount || 0).toFixed(2)}
                    </td>
                  </tr>
                )}
              {(purchaseOrder as any).amount_owed !== undefined && (
                <tr>
                  <td
                    colSpan={
                      purchaseOrder.status === 'placed'
                        ? 4
                        : purchaseOrder.status === 'verified' || purchaseOrder.status === 'paid'
                          ? 7
                          : 5
                    }
                    className="px-4 py-3 text-right text-sm font-semibold text-primary-600"
                  >
                    Amount Owed:
                  </td>
                  <td className="px-4 py-3 text-right text-base font-bold text-primary-600">
                    ₹{((purchaseOrder as any).amount_owed || 0).toFixed(2)}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Actions</h3>
        <div className="flex gap-3 flex-wrap">
          {canConfirm && (
            <Button
              variant="primary"
              onClick={() => handleStatusUpdate('confirmed')}
              disabled={isLoading}
            >
              <Icon name="Check" size={16} className="mr-2" />
              Confirm Order
            </Button>
          )}
          {canMarkShipped && (
            <Button
              variant="primary"
              onClick={() => handleStatusUpdate('out_for_delivery')}
              disabled={isLoading}
            >
              <Icon name="Truck" size={16} className="mr-2" />
              Mark Shipped
            </Button>
          )}
          {canMarkDelivered && (
            <Button
              variant="primary"
              onClick={() => setIsDeliveryDateModalOpen(true)}
              disabled={isLoading}
            >
              <Icon name="Package" size={16} className="mr-2" />
              Mark as Delivered
            </Button>
          )}
          {canReceive && (
            <Button variant="primary" onClick={handleOpenGRN} disabled={isLoading}>
              <Icon name="Package" size={16} className="mr-2" />
              Receive Goods
            </Button>
          )}
          {canVerify && (
            <Button
              variant="primary"
              onClick={handleVerifyClick}
              disabled={isLoading}
            >
              <Icon name="CircleCheck" size={16} className="mr-2" />
              Verify Order
            </Button>
          )}
          {canMarkPaid && (
            <Button
              variant="primary"
              onClick={() => handleStatusUpdate('paid')}
              disabled={isLoading}
            >
              <Icon name="DollarSign" size={16} className="mr-2" />
              Mark Paid
            </Button>
          )}
        </div>
      </div>

      <DeliveryModal
        purchaseOrder={purchaseOrder}
        open={isGRNModalOpen}
        onClose={() => setIsGRNModalOpen(false)}
        onSuccess={handleGRNSuccess}
      />

      <DeliveryModal
        purchaseOrder={updatedPurchaseOrder || purchaseOrder}
        open={isVerifyModalOpen}
        onClose={() => {
          setIsVerifyModalOpen(false);
          setUpdatedPurchaseOrder(null);
        }}
        onSuccess={handleVerifySuccess}
      />

      <DeliveryDateModal
        purchaseOrder={purchaseOrder}
        isOpen={isDeliveryDateModalOpen}
        onClose={() => setIsDeliveryDateModalOpen(false)}
        onSuccess={() => {
          setIsDeliveryDateModalOpen(false);
          onUpdate?.();
        }}
      />
    </div>
  );
};
